import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import apiClient from "../utils/apiClient";
import {
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import PageLayout from "../components/admin/PageLayout";

const EditCommittee = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [formData, setFormData] = useState({
    name: "",
    type: "auction",
    description: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  useEffect(() => {
    apiClient
      .get(`committee/${id}`)
      .then((res) => {
        if (res.data.success) {
          const d = res.data.data;
          setFormData({
            name: d.name || "",
            type: d.type || "auction",
            description: d.description || "",
          });
        }
      })
      .catch(() => {
        setSnackbar({ open: true, message: "Committee not found", severity: "error" });
      })
      .finally(() => setLoading(false));
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const response = await apiClient.put(`committee/${id}`, formData);
      if (response.data.success) {
        setSnackbar({ open: true, message: "Committee updated successfully", severity: "success" });
        navigate("/committees");
      } else {
        setSnackbar({ open: true, message: response.data.message || "Update failed", severity: "error" });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Failed to update committee",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <PageLayout title="Edit Committee" subtitle="Update committee details.">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={280}>
          <CircularProgress size={48} sx={{ color: "primary.main" }} />
        </Box>
      </PageLayout>
    );
  }

  return (
    <PageLayout title="Edit Committee" subtitle="Update committee details.">
    <Box sx={{ maxWidth: 560 }}>
      <form onSubmit={handleSubmit}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField
            label="Committee Name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            fullWidth
          />
          <FormControl fullWidth required>
            <InputLabel>Type</InputLabel>
            <Select name="type" value={formData.type} onChange={handleChange} label="Type">
              <MenuItem value="auction">Auction</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Description (optional)"
            name="description"
            value={formData.description}
            onChange={handleChange}
            multiline
            rows={3}
            fullWidth
          />
          <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
            <Button type="submit" variant="contained" color="primary" disabled={saving}>
              {saving ? <CircularProgress size={24} /> : "Save"}
            </Button>
            <Button variant="outlined" onClick={() => navigate("/committees")}>
              Cancel
            </Button>
          </Box>
        </Box>
      </form>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
    </PageLayout>
  );
};

export default EditCommittee;
